import React, { useMemo, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useToasts } from 'react-toast-notifications';
import {
  AcceptCaseTagSuggestionRequest,
  CaseTagSuggestion,
  RejectCaseTagSuggestionRequest,
} from '../types';
import styles from './CaseIntelligenceDashboard.module.scss';

interface Props {
  tagSuggestions: CaseTagSuggestion[] | null;
  tagSuggestionsPending: boolean;
  tagSuggestionsFeedback: { error?: unknown } | null;
  onRetry: () => void;
  onAcceptSuggestion?: (payload: AcceptCaseTagSuggestionRequest) => Promise<unknown> | unknown;
  onRejectSuggestion?: (payload: RejectCaseTagSuggestionRequest) => Promise<unknown> | unknown;
}

type TagSuggestionAction = 'accept' | 'save' | 'reject';

interface CategoryOption {
  categoryId: string;
  category: string;
}

interface TagSuggestionDraft {
  name: string;
  categoryId?: string;
}

function humanizeToken(value: string | null | undefined) {
  if (!value) {
    return 'Unknown';
  }

  return value
    .split(/[_-]/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function createSuggestionDraft(suggestion: CaseTagSuggestion): TagSuggestionDraft {
  return {
    name: suggestion.name,
    categoryId: suggestion.categoryId,
  };
}

function omitRecordKey<T extends Record<string, any>>(record: T, key: string): T {
  const rest = { ...record };
  delete rest[key];
  return rest as T;
}

const TagSuggestionsPanel: React.FC<Props> = ({
  tagSuggestions,
  tagSuggestionsPending,
  tagSuggestionsFeedback,
  onRetry,
  onAcceptSuggestion,
  onRejectSuggestion,
}) => {
  const { addToast } = useToasts();
  const [editingSuggestionName, setEditingSuggestionName] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, TagSuggestionDraft>>({});
  const [pendingActions, setPendingActions] = useState<Record<string, TagSuggestionAction>>({});
  const [handledSuggestionNames, setHandledSuggestionNames] = useState<Record<string, boolean>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});

  const categoryOptions = useMemo<CategoryOption[]>(() => {
    if (!tagSuggestions) return [];
    const seen = new Map<string, string>();
    for (const suggestion of tagSuggestions) {
      if (suggestion.categoryId && !seen.has(suggestion.categoryId)) {
        seen.set(suggestion.categoryId, suggestion.category);
      }
    }
    return Array.from(seen, ([categoryId, category]) => ({ categoryId, category })).sort(
      (a, b) => humanizeToken(a.category).localeCompare(humanizeToken(b.category)),
    );
  }, [tagSuggestions]);

  const visibleTagSuggestions =
    tagSuggestions?.filter((suggestion) => !handledSuggestionNames[suggestion.name]) || [];

  const clearActionError = (suggestionName: string) => {
    setActionErrors((current) =>
      current[suggestionName] ? omitRecordKey(current, suggestionName) : current,
    );
  };

  const handleActionStart = (suggestionName: string, action: TagSuggestionAction) => {
    setPendingActions((current) => ({
      ...current,
      [suggestionName]: action,
    }));
    clearActionError(suggestionName);
  };

  const handleActionFinish = (suggestionName: string) => {
    setPendingActions((current) =>
      current[suggestionName] ? omitRecordKey(current, suggestionName) : current,
    );
  };

  const handleActionSuccess = (suggestionName: string) => {
    setHandledSuggestionNames((current) => ({
      ...current,
      [suggestionName]: true,
    }));
    setDrafts((current) =>
      current[suggestionName] ? omitRecordKey(current, suggestionName) : current,
    );
    clearActionError(suggestionName);
    setEditingSuggestionName((current) => (current === suggestionName ? null : current));
  };

  const handleActionFailure = (suggestionName: string) => {
    setActionErrors((current) => ({
      ...current,
      [suggestionName]: "We couldn't update this suggestion. Try again.",
    }));
  };

  const runSuggestionAction = async ({
    suggestionName,
    action,
    request,
  }: {
    suggestionName: string;
    action: TagSuggestionAction;
    request: () => Promise<unknown> | unknown;
  }) => {
    handleActionStart(suggestionName, action);

    const actionLabels: Record<TagSuggestionAction, string> = {
      accept: 'accepted',
      reject: 'rejected',
      save: 'saved',
    };

    try {
      await Promise.resolve(request());
      handleActionSuccess(suggestionName);
      addToast(`Tag "${suggestionName}" ${actionLabels[action]} successfully.`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch {
      handleActionFailure(suggestionName);
      addToast(`Failed to ${action} tag "${suggestionName}". Please try again.`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      handleActionFinish(suggestionName);
    }
  };

  const startEditingSuggestion = (suggestion: CaseTagSuggestion) => {
    clearActionError(suggestion.name);
    setEditingSuggestionName(suggestion.name);
    setDrafts((current) =>
      current[suggestion.name]
        ? current
        : {
            ...current,
            [suggestion.name]: createSuggestionDraft(suggestion),
          },
    );
  };

  const cancelEditingSuggestion = (suggestion: CaseTagSuggestion) => {
    clearActionError(suggestion.name);
    setEditingSuggestionName((current) => (current === suggestion.name ? null : current));
    setDrafts((current) => ({
      ...current,
      [suggestion.name]: createSuggestionDraft(suggestion),
    }));
  };

  const updateSuggestionDraft = (
    suggestion: CaseTagSuggestion,
    key: keyof TagSuggestionDraft,
    value: string,
  ) => {
    clearActionError(suggestion.name);
    setDrafts((current) => ({
      ...current,
      [suggestion.name]: {
        ...(current[suggestion.name] || createSuggestionDraft(suggestion)),
        [key]: value,
      },
    }));
  };

  const acceptSuggestion = async (suggestion: CaseTagSuggestion) => {
    if (!onAcceptSuggestion) {
      return;
    }

    await runSuggestionAction({
      suggestionName: suggestion.name,
      action: 'accept',
      request: () =>
        onAcceptSuggestion({
          suggestionName: suggestion.name,
          name: suggestion.name,
          categoryId: suggestion.categoryId!,
        }),
    });
  };

  const saveEditedSuggestion = async (suggestion: CaseTagSuggestion) => {
    if (!onAcceptSuggestion) {
      return;
    }

    const draft = drafts[suggestion.name] || createSuggestionDraft(suggestion);
    const nextName = draft.name.trim();
    const nextCategoryId = draft.categoryId;

    if (!nextName || !nextCategoryId) {
      return;
    }

    await runSuggestionAction({
      suggestionName: suggestion.name,
      action: 'save',
      request: () =>
        onAcceptSuggestion({
          suggestionName: suggestion.name,
          name: nextName,
          categoryId: nextCategoryId!,
        }),
    });

    setHandledSuggestionNames({});
    setDrafts({});
    setActionErrors({});
    onRetry();
  };

  const handleEditKeyDown = async (
    event: React.KeyboardEvent<HTMLDivElement>,
    suggestion: CaseTagSuggestion,
    canSaveEdits: boolean,
  ) => {
    const target = event.target as HTMLElement;
    const isOptionSelection = Boolean(target.closest('[role="option"],[role="listbox"]'));

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditingSuggestion(suggestion);
      return;
    }

    if (event.key === 'Enter' && !isOptionSelection) {
      event.preventDefault();
      if (canSaveEdits) {
        await saveEditedSuggestion(suggestion);
      }
    }
  };

  const rejectSuggestion = async (suggestion: CaseTagSuggestion) => {
    if (!onRejectSuggestion) {
      return;
    }

    await runSuggestionAction({
      suggestionName: suggestion.name,
      action: 'reject',
      request: () =>
        onRejectSuggestion({
          suggestionName: suggestion.name,
        }),
    });
  };

  if (tagSuggestionsPending || (tagSuggestions === null && !tagSuggestionsFeedback)) {
    return (
      <div className={styles.tagStatePanel}>
        <div className={styles.sectionHeading}>Tag suggestions</div>
        <div className={styles.issueCardList}>
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className={styles.loadingCard}>
              <div className={styles.loadingTitle} />
              <div className={styles.loadingLine} />
              <div className={styles.loadingActions}>
                <div className={styles.loadingCircle} />
                <div className={styles.loadingCircle} />
                <div className={styles.loadingCircle} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tagSuggestionsFeedback) {
    return (
      <div className={styles.tagStatePanel}>
        <div className={styles.sectionHeading}>Tags unavailable</div>
        <p className={styles.supportingCopy}>
          We couldn&apos;t load tag suggestions for this case right now.
        </p>
        <button type="button" className={styles.retryButton} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (!visibleTagSuggestions.length) {
    return (
      <div className={styles.tagStatePanel}>
        <div className={styles.sectionHeading}>No tag suggestions yet</div>
        <p className={styles.supportingCopy}>
          Suggested tags will appear here once the intelligence service returns them.
        </p>
      </div>
    );
  }

  const groupedSuggestions = visibleTagSuggestions.reduce(
    (accumulator, suggestion) => {
      const key = suggestion.category || 'uncategorized';

      if (!accumulator[key]) {
        accumulator[key] = [];
      }

      accumulator[key].push(suggestion);
      return accumulator;
    },
    {} as Record<string, CaseTagSuggestion[]>,
  );

  const groups = Object.entries(groupedSuggestions)
    .map(([category, items]) => ({
      category,
      items: [...items].sort((left, right) => left.name.localeCompare(right.name)),
    }))
    .sort((left, right) =>
      humanizeToken(left.category).localeCompare(humanizeToken(right.category)),
    );

  return (
    <div className={styles.tagsPane}>
      {groups.map((group) => (
        <section key={group.category} className={styles.sectionBlock}>
          <div className={styles.sectionHeading}>{humanizeToken(group.category)}</div>
          <div className={styles.issueCardList}>
            {group.items.map((suggestion) => {
              const currentDraft = drafts[suggestion.name] || createSuggestionDraft(suggestion);
              const pendingAction = pendingActions[suggestion.name] || null;
              const isEditing = editingSuggestionName === suggestion.name;
              const isActionPending = Boolean(pendingAction);
              const nextName = currentDraft.name.trim();
              const nextCategoryId = currentDraft.categoryId;
              const hasChanges =
                nextName !== suggestion.name || nextCategoryId !== suggestion.categoryId;
              const canSaveEdits =
                Boolean(nextName && nextCategoryId) &&
                hasChanges &&
                Boolean(onAcceptSuggestion) &&
                !isActionPending;

              return (
                <article
                  key={`${group.category}-${suggestion.name}`}
                  className={`${styles.issueCard} ${styles.tagSuggestionCard} ${
                    isActionPending ? styles.pendingCard : ''
                  }`}
                >
                  <div className={`${styles.issueMain} ${styles.tagSuggestionHeader}`}>
                    <div>
                      <div className={styles.tagTitleRow}>
                        <h3 className={`${styles.cardTitle} ${styles.tagCardTitle}`}>
                          {suggestion.name}
                        </h3>
                        <span
                          className={styles.tagColorDot}
                          style={{ backgroundColor: suggestion.color }}
                          aria-label={`${suggestion.name} color`}
                        />
                      </div>
                    </div>

                    {!isEditing && (
                      <div className={`${styles.actionButtons} ${styles.tagSuggestionActions}`}>
                        <Tooltip title="Accept" arrow placement="top">
                          <span>
                            <button
                              type="button"
                              className={`${styles.actionButton} ${styles.actionButtonPositive}`}
                              onClick={() => acceptSuggestion(suggestion)}
                              disabled={!onAcceptSuggestion || isActionPending}
                              aria-label={`Accept ${suggestion.name}`}
                            >
                              <CheckRoundedIcon fontSize="inherit" />
                            </button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit" arrow placement="top">
                          <span>
                            <button
                              type="button"
                              className={styles.actionButton}
                              onClick={() => startEditingSuggestion(suggestion)}
                              disabled={!onAcceptSuggestion || isActionPending}
                              aria-label={`Accept with edits ${suggestion.name}`}
                            >
                              <EditOutlinedIcon fontSize="inherit" />
                            </button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reject" arrow placement="top">
                          <span>
                            <button
                              type="button"
                              className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                              onClick={() => rejectSuggestion(suggestion)}
                              disabled={!onRejectSuggestion || isActionPending}
                              aria-label={`Reject ${suggestion.name}`}
                            >
                              <CloseRoundedIcon fontSize="inherit" />
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div
                      className={styles.editForm}
                      onKeyDown={(event) => handleEditKeyDown(event, suggestion, canSaveEdits)}
                    >
                      <div className={styles.editFields}>
                        <TextField
                          className={`${styles.editField} ${styles.editFieldName}`}
                          size="small"
                          label="Tag name"
                          value={currentDraft.name}
                          onChange={(event) =>
                            updateSuggestionDraft(suggestion, 'name', event.target.value)
                          }
                          disabled={isActionPending}
                          fullWidth
                          inputProps={{ 'aria-label': 'Tag name' }}
                        />
                        <TextField
                          className={`${styles.editField} ${styles.editFieldCategory}`}
                          size="small"
                          label="Category"
                          value={currentDraft.categoryId}
                          onChange={(event) =>
                            updateSuggestionDraft(suggestion, 'categoryId', event.target.value)
                          }
                          disabled={isActionPending}
                          fullWidth
                          select
                          SelectProps={{ 'aria-label': 'Category' } as any}
                        >
                          {categoryOptions.map((option) => (
                            <MenuItem key={option.categoryId} value={option.categoryId}>
                              {humanizeToken(option.category)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </div>
                      <div className={styles.editActions}>
                        <button
                          type="button"
                          className={styles.editCancelButton}
                          onClick={() => cancelEditingSuggestion(suggestion)}
                          disabled={isActionPending}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={styles.editSaveButton}
                          onClick={() => saveEditedSuggestion(suggestion)}
                          disabled={!canSaveEdits}
                        >
                          Save &amp; accept
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {actionErrors[suggestion.name] ? (
                    <div role="alert" className={styles.actionError}>
                      {actionErrors[suggestion.name]}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default TagSuggestionsPanel;
